import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Loader2, HandCoins, CheckCircle2, ExternalLink, PlayCircle, Lock, ShieldCheck } from "lucide-react";

export default function Nsfas() {
  const [state, setState] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/nsfas/guide").then(({ data }) => setState(data)).catch(() => setState({ unlocked: false, unlock_fee: 26.99 }));
  }, []);

  const unlock = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/initialize", { type: "unlock", callback_url: `${window.location.origin}/payment/callback` });
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setPaying(false); }
  };

  if (!state) return <div className="min-h-screen bg-[#F8F9FA]"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div></div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 text-emerald-700"><HandCoins className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">NSFAS Funding Guide</span></div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1" data-testid="nsfas-title">Apply for NSFAS the right way</h1>

        {!state.unlocked ? (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center" data-testid="nsfas-locked">
            <Lock className="w-10 h-10 text-[#F5C32C] mx-auto" />
            <h2 className="font-display text-2xl font-bold text-[#0B2447] mt-4">Unlock the NSFAS guide</h2>
            <p className="text-slate-600 mt-2">Pay the once-off <span className="font-semibold text-[#0B2447]">R{Number(state.unlock_fee).toFixed(2)}</span> unlock fee to view the full NSFAS checklist, videos and application steps.</p>
            <p className="text-xs text-slate-400 mt-2">Note: NSFAS applications are free and done by you on the official platform — this fee only unlocks our guidance.</p>
            <PayMethods className="mt-5 max-w-md mx-auto" />
            <Button onClick={unlock} disabled={paying} data-testid="nsfas-unlock-btn" className="mt-5 bg-[#0B2447] hover:bg-[#153461] py-6 px-8 text-base">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Unlock for R${Number(state.unlock_fee).toFixed(2)}`}
            </Button>
          </div>
        ) : (
          <Guide guide={state.guide} />
        )}
      </div>
    </div>
  );
}

function Guide({ guide }) {
  return (
    <div className="mt-6 space-y-6" data-testid="nsfas-guide">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <p className="font-semibold text-emerald-800">{guide.season}</p>
        <p className="text-sm text-emerald-700 mt-1">{guide.free}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href={guide.apply_url} target="_blank" rel="noreferrer" data-testid="nsfas-apply-link" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2.5 rounded-md"><ExternalLink className="w-4 h-4" /> Apply on nsfas.org.za</a>
          <a href={guide.video_url} target="_blank" rel="noreferrer" data-testid="nsfas-video-link" className="inline-flex items-center gap-1.5 bg-white border border-emerald-300 text-emerald-700 font-semibold text-sm px-4 py-2.5 rounded-md"><PlayCircle className="w-4 h-4" /> Watch how-to videos</a>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /><span>{guide.note}</span></div>

      <section>
        <h2 className="font-display font-bold text-xl text-[#0B2447]">Get ready before applications open</h2>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {guide.get_ready.map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="w-7 h-7 rounded-full bg-[#0B2447] text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
              <h3 className="font-semibold text-[#0B2447] mt-2">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{s.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-bold text-xl text-[#0B2447]">Supporting documents checklist</h2>
        <div className="mt-3 space-y-3">
          {guide.checklist.map((c, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`nsfas-checklist-${i}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#0B2447]">{c.group}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.level === "Required" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{c.level}</span>
              </div>
              <ul className="mt-2 space-y-1.5">
                {c.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-bold text-xl text-[#0B2447]">Who can apply</h2>
        <ul className="mt-3 bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-1.5">
          {guide.who_can_apply.map((w, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {w}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
